import ReactMarkdown from "react-markdown"
import type { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import "katex/dist/katex.min.css"
import { cn } from "@/lib/utils"
import { normalizeMathFences } from "@/lib/normalize-math-fences"
import { normalizeListIndentation } from "@/lib/normalize-list-indentation"

// Display math (`$$…$$`) only. `singleDollarTextMath` is ON by default, which
// welds two currency amounts in one paragraph into an inline-math span: both `$`
// become delimiters and KaTeX typesets the prose between them as italic math
// letters ("$0.2150/hr and $0.4300/hr" → "0.2150/hr and 0.4300/hr"). In this
// corpus `$` is overwhelmingly currency. `$$…$$` still reaches KaTeX, which is
// the only thing that renders real math — an array environment or a nested
// subscript has no Unicode equivalent to fall back on.
const mathOptions = { singleDollarTextMath: false }

const proseClass = cn(
  "text-base font-light leading-relaxed text-foreground",
  "max-w-none break-words",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
  "[&_p]:my-2 [&_p]:leading-relaxed",
  "[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold",
  "[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold",
  "[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold",
  "[&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-muted-foreground",
  "[&_h5]:mt-2 [&_h5]:mb-1 [&_h5]:text-xs [&_h5]:font-semibold [&_h5]:uppercase [&_h5]:tracking-wide",
  "[&_h6]:mt-2 [&_h6]:mb-1 [&_h6]:text-xs [&_h6]:font-medium [&_h6]:text-muted-foreground",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1",
  "[&_li]:leading-relaxed [&_li>p]:my-0",
  // Nested lists: tighter than the gap between top-level items, and a marker
  // that differs per depth so the hierarchy reads without counting indents.
  "[&_li>ul]:my-1 [&_li>ol]:my-1",
  "[&_li>ul]:list-[circle] [&_li>ul>li>ul]:list-[square]",
  "[&_a]:underline [&_a]:underline-offset-2 [&_a]:font-medium",
  "[&_strong]:font-semibold",
  "[&_em]:italic",
  "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground",
  "[&_code]:rounded [&_code]:bg-background/60 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]",
  "[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-background/70 [&_pre]:p-3 [&_pre]:text-xs",
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[0.95em]",
  "[&_hr]:my-3 [&_hr]:border-border",
  "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
  "[&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold",
  "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
)

type MarkdownContentProps = {
  content: string
  className?: string
  components?: Components
}

export const MarkdownContent = ({
  content,
  className,
  components,
}: MarkdownContentProps) => (
  <div className={cn(proseClass, className)}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath, mathOptions]]}
      rehypePlugins={[rehypeKatex]}
      components={components}
    >
      {normalizeListIndentation(normalizeMathFences(content))}
    </ReactMarkdown>
  </div>
)
